import { useCallback, useRef, useState } from 'react'

/**
 * Taking a change back — the stack, and where it lives.
 *
 * **In the session, and nowhere else.** Every change carries its own undoing with it, the stack sits
 * in this component and it is never saved. A change log on the service would be a second truth
 * beside the coordinates — one that has to be kept in step with them, migrated, and cleaned up —
 * for a benefit that ends when the tab is closed.
 *
 * Why a board needs this at all, when a list does not: on a surface a slip weighs more. A frame let
 * go in the wrong place moves twenty cards at once, and there is nothing on the screen that says
 * where they were a moment ago.
 *
 * An undoing that fails is dropped rather than retried. It was written against a board that has
 * since moved on — something was deleted on the phone, a card was taken off — and putting it back
 * would be undoing something nobody did.
 */

export interface Change {
  /** What happened, in as few words as it takes. Shown on the control: "Undo moving". */
  readonly label: string
  /** Puts it back. Runs against the service, exactly as the change itself did. */
  readonly undo: () => Promise<void>
}

export interface History {
  /** What the next undo would take back, or `null` when there is nothing to take back. */
  readonly last: Change | null
  readonly remember: (change: Change) => void
  readonly undo: () => Promise<void>
  /** Empties the stack — when the board being looked at is no longer the one it was written for. */
  readonly forget: () => void
}

/**
 * How many steps back the stack holds.
 *
 * Enough for a run of slips, and a limit all the same: every entry holds a closure over the state it
 * has to restore, and an unbounded stack is an unbounded amount of it.
 */
export const STEPS_KEPT = 50

export function useHistory(): History {
  const stack = useRef<Change[]>([])
  // The stack itself is a ref, because nothing renders from it; this is the one thing that does —
  // whether there is anything to undo, and what it is called.
  const [last, setLast] = useState<Change | null>(null)

  const remember = useCallback((change: Change) => {
    stack.current = [...stack.current, change].slice(-STEPS_KEPT)
    setLast(change)
  }, [])

  const forget = useCallback(() => {
    stack.current = []
    setLast(null)
  }, [])

  const undo = useCallback(async () => {
    const taking = stack.current.at(-1)
    if (taking === undefined) {
      return
    }
    stack.current = stack.current.slice(0, -1)
    setLast(stack.current.at(-1) ?? null)
    try {
      await taking.undo()
    } catch {
      // Written against a board that has moved on since. Dropped rather than retried: putting
      // something back that somebody else has meanwhile taken away is not an undo.
    }
  }, [])

  return { last, remember, undo, forget }
}
